import { useRef, useCallback, type RefObject } from 'react'

import type { SavedReply } from '~publish/gql/graphql'

import { useCommentData } from '../../context/CommentContext'
import { useCommentsTracking } from '../../hooks/useCommentsTracking'

type UseReplyFeaturesProps = {
  text: string
  setText: (text: string) => void
  textareaRef: RefObject<HTMLTextAreaElement>
}

type UseReplyFeaturesReturn = {
  insertSuggestion: (text: string) => void
  insertSavedReply: (savedReply: SavedReply) => void
  savedRepliesRef: RefObject<HTMLDivElement>
  emojiPickerRef: RefObject<HTMLDivElement>
}

/**
 * Hook for managing reply form features and integrations.
 *
 * This hook handles feature-specific functionality including:
 * - Saved replies insertion with analytics tracking
 * - Suggestion text insertion
 * - Focus management after content insertion
 * - Ref management for saved replies popover
 *
 * **When to use:**
 * - When you need saved replies functionality
 * - For components that require suggestion insertion
 * - When you need analytics tracking for saved reply usage
 * - As part of a complete reply form experience
 *
 * **Key Features:**
 * - Smart text insertion (appends to existing text with space)
 * - Automatic focus after content insertion
 * - Analytics tracking for saved reply usage
 * - Ref management for popover positioning
 * - Comment context integration for tracking
 *
 * **Text Insertion Logic:**
 * - If current text is empty: inserts new text directly
 * - If current text exists: appends with space separator
 * - Always focuses the textarea after insertion
 * - Tracks usage analytics for saved replies
 *
 * @param props - Configuration for feature behavior
 * @returns Feature handlers and refs
 *
 * @example
 * ```tsx
 * const features = useReplyFeatures({
 *   text: replyText,
 *   setText: setReplyText,
 *   textareaRef
 * })
 *
 * // Basic usage
 * features.insertSuggestion("Thank you for your feedback!")
 * features.insertSavedReply(savedReply)
 *
 * <SavedRepliesPopover ref={features.savedRepliesRef} />
 * ```
 *
 * @example
 * ```tsx
 * // Custom handling
 * const handleQuickReply = (quickReply) => {
 *   if (replyText.length > 100) return
 *   features.insertSuggestion(quickReply)
 * }
 *
 * const handleSavedReply = (savedReply) => {
 *   if (savedReply.content.text.length > 500) return
 *   features.insertSavedReply(savedReply)
 * }
 * ```
 */
export const useReplyFeatures = ({
  text,
  setText,
  textareaRef,
}: UseReplyFeaturesProps): UseReplyFeaturesReturn => {
  const comment = useCommentData()
  const { trackSavedReplyUsed } = useCommentsTracking()

  const savedRepliesRef = useRef<HTMLDivElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)

  const insertText = useCallback(
    (newText: string) => {
      const currentText = text.trim()
      const finalText = currentText ? `${currentText} ${newText}` : newText
      setText(finalText)
      textareaRef.current?.focus()
    },
    [text, setText, textareaRef],
  )

  const insertSuggestion = useCallback(
    (suggestionText: string) => {
      insertText(suggestionText)
    },
    [insertText],
  )

  const insertSavedReply = useCallback(
    (savedReply: SavedReply) => {
      if (!savedReply) return
      const contentText = savedReply?.content?.text ?? ''
      insertText(contentText)
      trackSavedReplyUsed({
        comment,
        savedReply,
      })
    },
    [insertText, trackSavedReplyUsed, comment],
  )

  return {
    insertSuggestion,
    insertSavedReply,
    savedRepliesRef,
    emojiPickerRef,
  }
}
